import { datadogLogs } from '@datadog/browser-logs';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useEffect } from 'react';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useHouseholdAccountCount } from './hooks/useHouseholdAccountCount';
import {
  type SelectModelPortfolioEditTypeOptions,
  SelectModelPortfolioEditTypeVisual,
} from './selectModelPortfolioEditType.visual';

/**
 * Routes the household model portfolio edit workflow down one of its edit
 * branches.
 *
 * Nothing is persisted here: the chosen branch key is handed straight to
 * nextSubStep, which matches it against the branch an admin drew from the
 * matching handle in the workflow editor.
 */
export const SelectModelPortfolioEditType = ({
  options,
  branches,
  clientGroupId,
  onNext,
  stepLoading,
}: {
  options: SelectModelPortfolioEditTypeOptions;
  branches?: { fromKey?: string | null }[] | null;
  clientGroupId?: string;
  onNext: (branchKey?: string) => void | Promise<void>;
  stepLoading?: boolean;
}) => {
  const customizedAllocationsEnabled = useFeatureFlag(
    FlagsmithFlags.household_portfolio_customized_allocations_workflow,
  );

  // The count gates nothing but the customized choice, which the flag hides.
  const { count, error } = useHouseholdAccountCount(
    customizedAllocationsEnabled ? clientGroupId : undefined,
  );

  useEffect(() => {
    if (!error) return;

    datadogLogs.logger.error(
      'Household account count fetch failed',
      { clientGroupId },
      error,
    );
  }, [error, clientGroupId]);

  // A pending or failed lookup leaves the customized choice enabled: the Edit
  // Account Allocations step explains an unusable household itself.
  return (
    <SelectModelPortfolioEditTypeVisual
      options={options}
      loading={stepLoading}
      householdAccountCount={count}
      drawnBranchKeys={branches?.flatMap(({ fromKey }) =>
        fromKey ? [fromKey] : [],
      )}
      onNext={onNext}
    />
  );
};

export default SelectModelPortfolioEditType;
